import React, { useState, useContext } from "react";

import { TextField, Select, InputLabel, Button, Icon, Checkbox, FormControlLabel, MenuItem , CircularProgress, SelectChangeEvent } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Run from '../../types/run';
import GamesContext from '../../store/games-context';
import RunsContext from '../../store/runs-context';
import Game from '../../types/game';
import styles from './form.module.scss';
import { formatDate, sortByOrder, stringDateToDate } from "../../utils/utils";

interface FormProps {
  _id?: string;
  date?: string;
  order?: number;
  selectedGame?: number;
  reset?: boolean;
  partial?: boolean;
  includesHit?: boolean;
  splitHit?: number;
  startSplit?: number;
  endSplit?: number;
  clip?: string;
  handleSubmit: any;
  onCancel?: any
  onError?: any
  onSuccess?: any
};


const FormComponent = ({ 
  _id,
  date: initialDate,
  order: initialOrder,
  selectedGame: initialSelectedGame,
  reset: initialReset = false,
  partial: initialPartial = false,
  includesHit: initialHitValue = false,
  startSplit: initialStartSplit,
  endSplit: initialEndSplit,
  splitHit: initialSplitHit,
  clip: initialClip = '',
  handleSubmit,
  onSuccess,
  onError,
  onCancel 
} : FormProps ) => {
  const gamesContext = useContext(GamesContext);
  const runsContext = useContext(RunsContext);

  const games = gamesContext.games;

  const [includesHit, changeIncludesHit] = useState(initialHitValue);
  const [selectedGame, changeSelectedGame] = useState<number>(initialSelectedGame || 0);
  const [partial, changePartial] = useState<boolean>(initialPartial);
  const [reset, changeReset] = useState<boolean>(initialReset);
  const [date, updateDate] = useState<string>(initialDate || formatDate(new Date()));
  const [order, updateOrder] = useState(initialOrder || 1);
  const [splitHit, updateHitSplit] = useState<number | undefined>(initialSplitHit);
  const [splitHitString, updateHitSplitString] = useState<string | undefined>(`${initialSplitHit}`);
  const [startSplit, updateStartSplit] = useState<number | undefined>(initialStartSplit);
  const [startSplitString, updateStartSplitString] = useState<string | undefined>(`${initialStartSplit}`);
  const [endSplit, updateEndSplit] = useState<number | undefined>(initialEndSplit);
  const [endSplitString, updateEndSplitString] = useState<string | undefined>(`${initialEndSplit}`);
  const [clip, updateClip] = useState(initialClip);

  const clearForm = () => {
    changeSelectedGame(0);
    changePartial(false);
    changeReset(false);
    updateOrder(order + 1);
    updateHitSplit(undefined);
    updateHitSplitString(undefined);
    updateStartSplit(undefined);
    updateStartSplitString(undefined);
    updateEndSplit(undefined);
    updateEndSplitString(undefined);
    changeIncludesHit(false);
    updateClip('');
  }

  const updateIncludesHit = (value: boolean) => {
    updateHitSplit(undefined);
    updateHitSplitString(undefined);
    changeIncludesHit(value);
  }

  const updatePartial = (value: boolean) => {
    changePartial(value);
    updateStartSplit(undefined);
    updateStartSplitString(undefined);
    updateEndSplit(undefined);
    updateEndSplitString(undefined);
  }

  const updateHitSplitSelector = (value: string) => {
    updateHitSplitString(value);
    updateHitSplit(parseInt(value, 10));
  };

  const updateStartSplitSelector = (value: string) => {
    updateStartSplitString(value);
    updateStartSplit(parseInt(value, 10));
  };

  const updateEndSplitSelector = (value: string) => {
    updateEndSplitString(value);
    updateEndSplit(parseInt(value, 10));
  };

  const listSplits = (game: Game) => {
    const splitsSorted = game?.splits?.sort(sortByOrder);
    return (splitsSorted?.map(split =>
      <MenuItem key={split.id} value={split.id}>{split.name}</MenuItem>
    ));
  }

  const showHitSelector = () =>
  (<div className={styles.formitem} >
    <InputLabel id="splitHit">Split Hit</InputLabel>
    <Select className={styles.select} labelId="splitHit" id="splitHitSelect" value={splitHitString} onChange={(e: SelectChangeEvent)  => updateHitSplitSelector((e.target as HTMLSelectElement).value)}>
      {listSplits(games[selectedGame])}
    </Select>

  </div>
  );

  const showStartAndEndSelectors = () => {
    return (<>
      <div className={styles.formitem} >
        <InputLabel id="splitStart">Split Start</InputLabel>
        <Select 
          className={styles.select}
          labelId="splitStart"
          id="splitStartSelect"
          value={startSplitString} 
          onChange={
            (event: SelectChangeEvent) => updateStartSplitSelector(event.target.value)
          }>
          {listSplits(games[selectedGame])}
        </Select>
      </div>
      <div className={styles.formitem} >
        <InputLabel id="splitEnds">Split End</InputLabel>
        <Select className={styles.select} labelId="splitEnd" id="splitEndSelect" value={endSplitString} onChange={(e: SelectChangeEvent) => updateEndSplitSelector((e.target as HTMLSelectElement).value)}>
          {listSplits(games[selectedGame])}
        </Select>
      </div>
    </>
    );
  }
 
  if (games && games.length > 0) {
    const listGames = games.map(game =>
      <MenuItem key={game.id} value={game.id}>{game.name}</MenuItem>
    );
      return (
        <div>
        <form className={styles.container} noValidate onSubmit={(e) => {
          const run = ({ 
            _id,
            date,
            order,
            selectedGame,
            reset,
            partial,
            includesHit,
            startSplit,
            endSplit,
            splitHit,
            clip 
            } as Run);
          clearForm();
          return handleSubmit({ event: e, run, runsContext, onSuccess, onError });
        }}>
            <div className={styles.formitem} >
              <DatePicker
                label="Run Date"
                className={styles.textField}
                value={stringDateToDate(date)}
                onChange={e => e && updateDate(formatDate(new Date(e)))}
                renderInput={(params) => <TextField {...params} />}
              />
            </div>
          <div className={styles.formitem} >
            <TextField
              id="order"
              label="Run number in the day"
              type="number"
              className={styles.textField}
              value={order}
              onInput={e => updateOrder(parseInt((e.target as HTMLInputElement).value, 10))}
            />
          </div>
          <div className={styles.formitem} >
            <InputLabel id="game">Game</InputLabel>
            <Select className={styles.select} labelId="game" id="gameSelect" value={selectedGame} onChange={e => {
              const value : string = (e.target as HTMLInputElement).value;
              changeSelectedGame(parseInt(value, 10));
            }}>
              {listGames}
            </Select>
          </div>
          <div className={styles.formitem} >
            <FormControlLabel
              control={
                <Checkbox
                  checked={reset}
                  onChange={() => changeReset(!reset)}
                  name="reset"
                  color="primary"
                />
              }
              label="Reset Run?"
            />
          </div>
          <div className={styles.formitem} >
            <FormControlLabel
              control={
                <Checkbox
                  checked={partial}
                  onChange={() => updatePartial(!partial)}
                  name="partial"
                  color="primary"
                />
              }
              label="Partial Run?"
            />
          </div>
          
          {
            (partial ?
              (showStartAndEndSelectors())
              : null)
          }
          <div className={styles.formitem} >
            <FormControlLabel
              control={
                <Checkbox
                  checked={includesHit}
                  onChange={() => updateIncludesHit(!includesHit)}
                  name="includesHit"
                  color="primary"
                />
              }
              label="Hit?"
            />
          </div>
          {
            (includesHit ? (showHitSelector())
              : null)
          }
          <div className={styles.formitem} >
            <TextField multiline rows={4} id="run-clip" label="Clip" className={styles.clip} value={clip}
            onInput={e => updateClip((e.target as HTMLInputElement).value)}
            />
          </div>
          <Button variant="contained" color="secondary" endIcon={<Icon>send</Icon>} type="submit">
            Submit
          </Button>
          {
            onCancel && 
          <Button variant="outlined" color="secondary" onClick={onCancel}>
            Cancel
          </Button>
          }
        </form>
      </div>
      );
  } else {
    return (
    <div className={styles.loaderContainer}>
      <CircularProgress className={styles.loader} />
    </div>
    );
  }
};

export default FormComponent;